import { useEffect, useRef, useState } from "react";
import {
  createDocumentId,
  type DocumentRecord,
  forgetDocument,
  listDocuments,
  touchDocument,
} from "../../lib/documentsRegistry.ts";
import { readPanelSpecFile, setPendingImport } from "../export/share.ts";
import { setPendingTemplate } from "../templates/apply.ts";
import { findTemplate } from "../templates/catalog.ts";
import { TemplateGallery } from "./TemplateGallery.tsx";

/**
 * Dropdown för att byta mellan lokala centraler. Visar titel + senast redigerad.
 * "Ny" skapar ett nytt doc-id och navigerar dit.
 */
export function DocumentSwitcher({
  currentId,
  currentTitle,
  onSwitch,
}: {
  currentId: string;
  currentTitle: string;
  onSwitch: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState<DocumentRecord[]>([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) setDocs(listDocuments());
  }, [open]);

  // Uppdatera registret när titeln ändras.
  useEffect(() => {
    touchDocument(currentId, { title: currentTitle });
  }, [currentId, currentTitle]);

  const openGallery = () => {
    setOpen(false);
    setGalleryOpen(true);
  };

  const pickTemplate = (templateKey: string | null) => {
    const id = createDocumentId();
    const title = templateKey
      ? findTemplate(templateKey)?.name ?? "Ny central"
      : "Ny central";
    touchDocument(id, { title });
    if (templateKey) setPendingTemplate(id, templateKey);
    setGalleryOpen(false);
    onSwitch(id);
  };

  const remove = (id: string) => {
    forgetDocument(id);
    setDocs(listDocuments());
  };

  const onImportClick = () => {
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // Återställ så samma fil kan väljas igen.
    if (!file) return;
    try {
      const payload = await readPanelSpecFile(file);
      const id = createDocumentId();
      const title = payload.panel?.title ?? "Importerad central";
      touchDocument(id, { title });
      setPendingImport(id, payload);
      setOpen(false);
      onSwitch(id);
    } catch (err) {
      alert(
        err instanceof Error
          ? `Import misslyckades: ${err.message}`
          : "Import misslyckades: okänt fel.",
      );
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 rounded border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50"
      >
        <span className="max-w-xs truncate">{currentTitle || "Central"}</span>
        <span className="text-slate-400">▾</span>
      </button>
      {open ? (
        <div
          className="absolute left-0 top-full z-40 mt-1 w-80 rounded border border-slate-200 bg-white shadow-lg"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="flex flex-col gap-1 border-b border-slate-200 p-2">
            <button
              type="button"
              onClick={openGallery}
              className="w-full rounded bg-sky-600 px-3 py-1 text-sm text-white hover:bg-sky-700"
            >
              + Ny central…
            </button>
            <button
              type="button"
              onClick={onImportClick}
              className="w-full rounded border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50"
            >
              ⇪ Importera .panelspec…
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={onFileSelected}
              className="hidden"
            />
          </div>
          <ul className="max-h-80 overflow-auto">
            {docs.length === 0 ? (
              <li className="p-3 text-center text-xs text-slate-500">
                Inga sparade centraler än.
              </li>
            ) : (
              docs.map((d) => (
                <li
                  key={d.id}
                  className={`flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-slate-50 ${
                    d.id === currentId ? "bg-sky-50" : ""
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onSwitch(d.id);
                    }}
                    className="flex-1 text-left"
                  >
                    <div className="font-medium">{d.title || d.id}</div>
                    <div className="text-xs text-slate-500">
                      {new Date(d.lastEditedAt).toLocaleString("sv-SE")}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(d.id)}
                    className="text-xs text-slate-400 hover:text-red-500"
                    title="Ta bort från lista"
                  >
                    ×
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
      {galleryOpen ? (
        <TemplateGallery
          onPick={pickTemplate}
          onClose={() => setGalleryOpen(false)}
        />
      ) : null}
    </div>
  );
}
